import useAdminDataStore, { selectCurrentGroupData } from "../../../store/adminData/adminData";
import { useEffect, useState, useCallback } from "react";
import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  where,
} from "firebase/firestore";
import { db } from "../../../services/firebaseService/connection";
import logger from "../../../utils/logger";

const useChildrenGroups = () => {
  const groupData = useAdminDataStore(selectCurrentGroupData);
  const [childrenGroupIds, setChildrenGroupIds] = useState([]);
  const [childGroupDetails, setChildGroupDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const getAllClubGroups = useCallback(async (clubId) => {
    try {
      const groupIds = [];
      const groupDetails = [];

      const clubSnap = await getDocs(
        query(collection(db, "group"), where("clubId", "==", clubId))
      );
      clubSnap.docs.forEach((doc) => {
        groupIds.push(doc.id);
        groupDetails.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return { groupIds, groupDetails };
    } catch (error) {
      logger.error("Failed to fetch all club group IDs", error);
      return { groupIds: [], groupDetails: [] };
    }
  }, []);

  const getOfficialGroups = useCallback(async (clubId) => {
    try {
      const groupIds = [];
      const groupDetails = [];

      const childrenGroupsSnap = await getDocs(
        query(
          collection(db, "group"),
          where("clubId", "==", clubId),
          where("groupType", "==", "Official Supporters Group")
        )
      );

      childrenGroupsSnap.docs.forEach((doc) => {
        groupIds.push(doc.id);
        groupDetails.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      return { groupIds, groupDetails };
    } catch (error) {
      logger.error("Failed to fetch official group IDs", error);
      return { groupIds: [], groupDetails: [] };
    }
  }, []);

  const resolveChildrenGroups = useCallback(
    async (children, clubId) => {
      if (!Array.isArray(children) || children.length === 0) {
        return { groupIds: [], groupDetails: [] };
      }

      if (children.includes("All")) {
        return await getAllClubGroups(clubId);
      }

      if (children.includes("Official Groups")) {
        return await getOfficialGroups(clubId);
      }

      // For specific children IDs, fetch their details
      try {
        const detailPromises = children.map(async (groupId) => {
          try {
            const docRef = doc(db, "group", groupId);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
              return {
                id: docSnap.id,
                ...docSnap.data(),
              };
            }
            return null;
          } catch (error) {
            logger.error(`Failed to fetch group details for ${groupId}`, error);
            return null;
          }
        });

        const currentGroupPromise = getDoc(
          doc(db, "group", groupData?.id)
        ).then((doc) => {
          return {
            id: doc.id,
            ...doc.data(),
          };
        });
        detailPromises.push(currentGroupPromise);

        const details = await Promise.all(detailPromises);
        const groupDetails = details.filter((d) => d !== null);

        return { groupIds: children, groupDetails };
      } catch (error) {
        logger.error("Failed to fetch group details", error);
        return { groupIds: children, groupDetails: [] };
      }
    },
    [getAllClubGroups, getOfficialGroups, groupData?.id]
  );

  useEffect(() => {
    const fetchChildren = async () => {
      if (groupData?.isParent && groupData?.children && groupData?.clubId) {
        try {
          setIsLoading(true);

          const resolved = await resolveChildrenGroups(
            groupData.children,
            groupData.clubId
          );
          setChildrenGroupIds(resolved.groupIds);
          setChildGroupDetails(resolved.groupDetails);
        } catch (error) {
          logger.error("Failed to resolve children groups", error);
          setChildrenGroupIds(groupData.children);
          setChildGroupDetails([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        setChildrenGroupIds(groupData?.children || []);
        setChildGroupDetails([]);
      }
    };

    fetchChildren();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupData?.id]);

  return { childrenGroupIds, childGroupDetails, isLoading };
};

export default useChildrenGroups;
